/* eslint-disable */
/**
 * Construcción de la matriz de resumen de métricas.
 *
 * Estos métodos operan sobre la instancia de PanelLogic y conservan el
 * contrato del prototipo importado. No mantienen estado global propio.
 */
const logic = {
  smVals(st) {
    const D = this.data,
      N = D.days.length,
      C = D.rows,
      SD = this.scoreData(),
      day = st.day,
      norm = st.smNorm || 'tot',
      win = st.smWin || 'all';
    const sum = (a, g) => a.reduce((s, x) => s + (g(x) || 0), 0),
      nf = v => Math.round(v).toLocaleString('es-CL');
    // delivered_food already comes expressed in tonnes from SCAPP.
    const kgF = c => c.ay || !Number.isFinite(c.kg) ? 0 : c.kg;
    const RED = 'oklch(0.7 0.18 28)',
      GRN = 'oklch(0.78 0.14 155)',
      AMB = 'oklch(0.8 0.14 75)',
      NEU = 'oklch(0.78 0.1 225)';
    const alF = cs => {
      // V1 consumes these backend metrics directly and accumulates their daily
      // values; it never rebuilds either ratio from alarms, visits or biomass.
      const metric = norm === 'ton' ? 'apt' : norm === 'vis' ? 'apv' : 'alarms';
      return this.v1MetricAggregate(metric, cs) ?? 0;
    };
    const alFmt = v => norm === 'tot' ? nf(v) : v.toFixed(2),
      alLab = norm === 'tot' ? 'Alarmas' : norm === 'ton' ? 'Alarmas / t' : 'Alarmas / visita';
    const sfrF = cs => this.v1MetricAggregate('sfr', cs);
    const range = (a, b) => {
      const o = [];
      for (let d = Math.max(0, a); d <= Math.min(N - 1, b); d++) o.push(d);
      return o;
    };
    const cellsOf = (ds, ci) => ci == null ? ds.flatMap(d => C.map(r => r.cells[d])) : ds.map(d => C[ci].cells[d]);
    const W7 = range(day - 6, day),
      P7 = range(day - 13, day - 7);
    const WD = win === 'hoy' ? [day] : win === '7' ? W7 : range(0, N - 1),
      winLab = win === 'hoy' ? 'día ' + D.days[day] : win === '7' ? 'últimos 7 días (' + D.days[W7[0]] + ' – ' + D.days[day] + ')' : 'período completo';
    // KPIs
    const dayCells = d => C.map(r => r.cells[d]);
    const kdefs = [{
      l: alLab,
      c: 'oklch(0.8 0.13 85)',
      g: alF,
      f: alFmt,
      bad: 1
    }, {
      l: 'Pellets perdidos',
      c: 'oklch(0.66 0.19 25)',
      g: cs => this.v1MetricAggregate('ppa', cs) ?? 0,
      f: nf,
      bad: 1
    }, {
      l: 'SFR promedio',
      c: 'oklch(0.7 0.17 45)',
      g: sfrF,
      f: v => v.toFixed(1) + ' %',
      sfr: true
    }, {
      l: 'Alimento entregado',
      c: 'oklch(0.74 0.12 230)',
      g: cs => sum(cs, kgF),
      f: v => v.toLocaleString('es-CL', { maximumFractionDigits: 3 }) + ' t',
      bad: null
    }];
    // Header totals follow the same window as the matrix. A missing day must
    // not turn into a reported zero or hide measurements from the full period.
    const kpiEnd = win === 'all' ? N - 1 : day,
      kpiDays = range(kpiEnd - 13, kpiEnd),
      currentWeek = range(kpiEnd - 6, kpiEnd),
      previousWeek = range(kpiEnd - 13, kpiEnd - 7);
    const smKpis = kdefs.map(m => {
      const aggregate = cs => {
        const reported = cs.filter(c => c && !c.missing);
        return reported.length ? m.g(reported) : null;
      };
      const ser = kpiDays.map(d => aggregate(dayCells(d))),
        v = aggregate(cellsOf(WD));
      const avg = ds => {
          const x = ds.map(d => aggregate(dayCells(d))).filter(Number.isFinite);
          return x.length ? x.reduce((a, b) => a + b, 0) / x.length : null;
        },
        a = avg(currentWeek),
        b = avg(previousWeek);
      let d = '–',
        dc = '#8793a6';
      if (a != null && b != null) {
        if (m.sfr) {
          const dd = a - b;
          d = (dd >= 0 ? '▲ +' : '▼ ') + dd.toFixed(1) + ' pts';
          dc = Math.abs(a - 100) <= Math.abs(b - 100) ? GRN : RED;
        } else if (b) {
          const p = (a - b) / Math.abs(b) * 100;
          d = (p > 0.5 ? '▲ +' : p < -0.5 ? '▼ ' : '• ') + p.toFixed(0) + ' %';
          dc = m.bad == null || Math.abs(p) < 0.5 ? NEU : p > 0 === (m.bad === 1) ? RED : GRN;
        }
      }
      const vs = ser.filter(x => x != null),
        lo = m.sfr ? Math.min(80, ...vs) : 0,
        hi = Math.max(lo + 1e-9, m.sfr ? Math.max(110, ...vs) : Math.max(...vs, 1e-9)),
        Y = x => (40 - (x - lo) / (hi - lo) * 34).toFixed(1);
      const samples = ser.map((x, j) => x == null ? null : [(j / Math.max(1, ser.length - 1) * 136 + 2).toFixed(1), Y(x)]);
      const segments = [];
      samples.forEach((point, index) => {
        if (!point) return;
        if (!samples[index - 1]) segments.push([]);
        segments[segments.length - 1].push(point);
      });
      const pts = samples.filter(Boolean),
        line = segments.map(segment => 'M' + segment.map(point => point.join(' ')).join(' L')).join(' ') || 'M0 0',
        area = segments.map(segment => 'M' + segment.map(point => point.join(' ')).join(' L') + ' L' + segment[segment.length - 1][0] + ' 44 L' + segment[0][0] + ' 44 Z').join(' ') || 'M0 0';
      return {
        l: m.l,
        c: m.c,
        v: v == null ? 'Sin datos' : m.f(v),
        d,
        dc,
        sub: winLab + ' · prom. 7 d vs 7 previos',
        line,
        area,
        hasSeries: pts.length > 0,
        ref: m.sfr ? 'M0 ' + Y(100) + ' H140' : 'M0 0',
        lx: pts.length ? pts[pts.length - 1][0] : '0',
        ly: pts.length ? pts[pts.length - 1][1] : '0'
      };
    });
    // Matrix
    const cols = [{
      k: 'al',
      l: alLab,
      g: alF,
      f: alFmt,
      sev: 'rel'
    }, {
      k: 'ppa',
      l: 'Pellets perdidos',
      g: cs => this.v1MetricAggregate('ppa', cs) ?? 0,
      f: nf,
      sev: 'rel'
    }, {
      k: 'sfr',
      l: 'SFR',
      g: sfrF,
      f: v => v.toFixed(0) + ' %',
      sev: 'sfr'
    }, {
      k: 'ay',
      l: 'Ayunos',
      g: cs => cs.filter(c => c.ay).length,
      f: nf,
      sev: 'ay'
    }, {
      k: 'vis',
      l: 'Visitas',
      g: cs => this.v1MetricAggregate('visitas', cs) ?? 0,
      f: nf,
      sev: null
    }, {
      k: 'sc',
      l: 'Score',
      g: null,
      f: v => v.toFixed(0),
      sev: 'sc'
    }];
    const scOf = (ci, ds) => {
      const v = ds.map(d => SD.M[ci][d]).filter(Boolean);
      return v.length ? v.reduce((a, c) => a + c.score, 0) / v.length : null;
    };
    const val = (col, ci, ds) => col.k === 'sc' ? scOf(ci, ds) : col.g(cellsOf(ds, ci));
    const V = C.map((_, ci) => cols.map(col => val(col, ci, WD))),
      means = cols.map((_, j) => {
        const x = V.map(r => r[j]).filter(v => v != null);
        return x.length ? x.reduce((a, b) => a + b, 0) / x.length : 0;
      });
    const sevOf = (col, v, j) => {
      if (v == null) return 0;
      if (col.sev === 'rel') {
        const m = means[j];
        if (!m) return 0;
        const q = v / m;
        return q >= 1.6 ? 3 : q >= 1.2 ? 1 : 0;
      }
      if (col.sev === 'sfr') {
        // Un SFR sobre la meta puede requerir atencion, pero no es critico.
        // La prioridad critica queda reservada para el SFR insuficiente.
        if (v >= 100) return v > 105 ? 1 : 0;
        const deficit = 100 - v;
        return deficit > 12 ? 3 : deficit > 5 ? 1 : 0;
      }
      if (col.sev === 'ay') return v >= 2 ? 3 : v >= 1 ? 1 : 0;
      if (col.sev === 'sc') return v < 50 ? 3 : v < 62 ? 1 : 0;
      return 0;
    };
    const SEV = {
      3: {
        bg: 'oklch(0.58 0.19 25 / 0.38)',
        c: 'oklch(0.9 0.08 25)'
      },
      1: {
        bg: 'oklch(0.75 0.15 75 / 0.2)',
        c: 'oklch(0.9 0.1 80)'
      },
      0: {
        bg: 'rgba(46,179,238,0.04)',
        c: '#b8c1ce'
      }
    };
    const rowsA = C.map((row, ci) => {
      const sv = cols.map((col, j) => sevOf(col, V[ci][j], j));
      return {
        ci,
        sv,
        att: sv.reduce((a, b) => a + b, 0),
        bad: sv.filter(x => x === 3).length
      };
    }).sort((a, b) => b.att - a.att || b.bad - a.bad || a.ci - b.ci);
    const hc = st.smCell,
      go = (ci, d) => () => this.setState({
        sel: ci,
        ...(d != null ? {
          day: d
        } : {}),
        simple: false,
        view: 'panel',
        smCell: null,
        smHov: null
      });
    const smRows = rowsA.map((x, ri) => ({
      n: String(ri + 1),
      name: C[x.ci].name,
      go: go(x.ci),
      att: String(x.att),
      attBg: x.bad ? 'oklch(0.58 0.19 25 / 0.5)' : x.att ? 'oklch(0.75 0.15 75 / 0.3)' : 'rgba(46,179,238,0.08)',
      attC: x.bad ? '#fff' : x.att ? 'oklch(0.92 0.08 80)' : '#5a667a',
      nameC: x.ci === st.sel ? 'oklch(0.85 0.1 225)' : '#e4e9f0',
      rowBg: hc && hc.ri === ri ? 'rgba(46,179,238,0.06)' : 'transparent',
      cells: cols.map((col, j) => {
        const v = V[x.ci][j],
          s = SEV[x.sv[j]],
          on = hc && hc.ri === ri && hc.j === j;
        return {
          v: v == null ? '–' : col.f(v),
          bg: s.bg,
          c: s.c,
          fw: x.sv[j] === 3 ? 600 : 400,
          ol: on ? "0.092593vh solid #e4e9f0" : 'none',
          enter: () => this.setState({
            smCell: {
              ri,
              j,
              ci: x.ci
            }
          })
        };
      })
    }));
    let smTip = {
      on: false
    };
    if (hc && hc.ri < smRows.length) {
      const col = cols[hc.j],
        ds = range(day - 13, day),
        ser = ds.map(d => val(col, hc.ci, [d])),
        vs = ser.filter(v => v != null),
        mxv = Math.max(col.k === 'sfr' ? 110 : 1e-9, ...vs),
        bw = 140 / ds.length;
      const bars = ser.map((v, q) => v == null ? '' : 'M' + (q * bw + bw * 0.15).toFixed(1) + ' 40V' + (40 - v / mxv * 36).toFixed(1) + 'h' + (bw * 0.7).toFixed(1) + 'V40Z').join(''),
        right = hc.j >= 4;
      smTip = {
        on: true,
        title: C[hc.ci].name + ' · ' + col.l,
        sub: 'últimos 14 días · hasta ' + D.days[day],
        v: V[hc.ci][hc.j] == null ? '–' : col.f(V[hc.ci][hc.j]),
        vl: winLab,
        avg: 'Promedio jaulas: ' + col.f(means[hc.j]),
        bars: bars || 'M0 0',
        ref: col.k === 'sfr' ? 'M0 ' + (40 - 100 / mxv * 36).toFixed(1) + ' H140' : 'M0 0',
        left: right ? "calc(10.208333vw + (100% - 10.208333vw) * " + (hc.j / 6).toFixed(4) + " - 0.416667vw)" : "calc(10.208333vw + (100% - 10.208333vw) * " + ((hc.j + 1) / 6).toFixed(4) + " + 0.416667vw)",
        tx: right ? 'translate(-100%,-50%)' : 'translate(0,-50%)',
        top: ((hc.ri + 0.5) / smRows.length * 100).toFixed(2) + '%'
      };
    }
    // Alerts
    const al = [];
    C.forEach((row, ci) => {
      const cs = row.cells;
      let k = 0;
      for (let d = day; d >= 0 && !cs[d].ay && cs[d].sfr < 90; d--) k++;
      if (k >= 2) al.push({
        ci,
        s: 3,
        m: k,
        txt: 'SFR bajo 90 % hace ' + k + ' días',
        d: day
      });
      let o = 0;
      for (let d = day; d >= 0 && !cs[d].ay && cs[d].sfr > 115; d--) o++;
      if (o >= 2) al.push({
        ci,
        s: 1,
        m: o,
        txt: 'SFR sobre 115 % hace ' + o + ' días',
        d: day
      });
      const a7 = W7.reduce((s, d) => s + cs[d].al, 0),
        p7 = P7.reduce((s, d) => s + cs[d].al, 0);
      if (a7 >= 5 && p7 > 0 && a7 / p7 >= 1.5) {
        const p = Math.round((a7 / p7 - 1) * 100);
        al.push({
          ci,
          s: p >= 100 ? 3 : 1,
          m: p,
          txt: 'Alarmas +' + p + ' % vs semana anterior (' + a7 + ' vs ' + p7 + ')',
          d: W7.reduce((b, d) => cs[d].al > cs[b].al ? d : b, W7[0])
        });
      }
      const ay = W7.filter(d => cs[d].ay);
      if (ay.length >= 2) al.push({
        ci,
        s: 1,
        m: ay.length,
        txt: ay.length + ' ayunos esta semana',
        d: ay[ay.length - 1]
      });
      const pp = W7.reduce((s, d) => s + cs[d].ppa, 0),
        pm = C.reduce((s, r) => s + W7.reduce((q, d) => q + r.cells[d].ppa, 0), 0) / C.length;
      if (pm && pp / pm >= 1.5) al.push({
        ci,
        s: pp / pm >= 2 ? 3 : 1,
        m: pp / pm * 10,
        txt: 'Pellets perdidos ' + (pp / pm).toFixed(1) + '× el promedio (7 d)',
        d: W7.reduce((b, d) => cs[d].ppa > cs[b].ppa ? d : b, W7[0])
      });
    });
    al.sort((a, b) => b.s - a.s || b.m - a.m);
    const smAlerts = al.map(a => ({
      name: C[a.ci].name,
      txt: a.txt,
      day: D.days[a.d],
      bar: a.s === 3 ? 'oklch(0.62 0.2 25)' : 'oklch(0.78 0.15 75)',
      lvl: a.s === 3 ? 'Alta' : 'Media',
      lvlC: a.s === 3 ? 'oklch(0.75 0.15 25)' : 'oklch(0.85 0.12 80)',
      go: go(a.ci, a.d)
    }));
    // Timeline
    const hv = st.smHov,
      alS = range(0, N - 1).map(d => alF(dayCells(d))),
      sfS = range(0, N - 1).map(d => sfrF(dayCells(d))),
      aMx = Math.max(1e-9, ...alS) * 1.1,
      sLo = 70,
      sHi = 130,
      bw = 1000 / N;
    const Ys = v => (100 - (Math.max(sLo, Math.min(sHi, v)) - sLo) / (sHi - sLo) * 100).toFixed(2);
    const tlBars = alS.map((v, d) => 'M' + (d * bw + bw * 0.18).toFixed(1) + ' 100V' + (100 - v / aMx * 100).toFixed(1) + 'h' + (bw * 0.64).toFixed(1) + 'V100Z').join('');
    const sp = sfS.map((v, d) => v == null ? null : ((d + 0.5) * bw).toFixed(1) + ' ' + Ys(v)).filter(Boolean);
    let cq = 0;
    const ppC = range(0, N - 1).map(d => cq += sum(dayCells(d), c => c.ppa)),
      cMx = Math.max(1, cq) * 1.04;
    const tlCum = 'M' + ppC.map((v, d) => ((d + 0.5) * bw).toFixed(1) + ' ' + (100 - v / cMx * 100).toFixed(2)).join(' L'),
      tlCumArea = tlCum + ' L' + ((N - 0.5) * bw).toFixed(1) + ' 100 L' + (0.5 * bw).toFixed(1) + ' 100 Z';
    const tlTip = hv != null ? {
      on: true,
      L: ((hv + 0.5) / N * 100).toFixed(2) + '%',
      tx: hv / N > 0.7 ? "translate(calc(-100% - 0.520833vw),0)" : "translate(0.520833vw,0)",
      title: D.days[hv] + (hv === day ? ' · seleccionado' : ''),
      rows: [{
        l: alLab,
        c: 'oklch(0.8 0.13 85)',
        v: alFmt(alS[hv])
      }, {
        l: 'SFR promedio',
        c: 'oklch(0.7 0.17 45)',
        v: sfS[hv] == null ? '–' : sfS[hv].toFixed(1) + ' %'
      }, {
        l: 'Pellets perdidos',
        c: 'oklch(0.66 0.19 25)',
        v: nf(sum(dayCells(hv), c => c.ppa))
      }, {
        l: 'PPA acumulado',
        c: 'oklch(0.72 0.16 350)',
        v: nf(ppC[hv])
      }]
    } : {
      on: false,
      rows: []
    };
    const seg = (list, cur, field) => list.map(([k, label]) => ({
      label,
      bg: cur === k ? 'rgba(46,179,238,0.16)' : 'transparent',
      color: cur === k ? '#e4e9f0' : '#8793a6',
      fw: cur === k ? 600 : 400,
      onClick: () => this.setState({
        [field]: k,
        smCell: null
      })
    }));
    return {
      smKpis,
      smRows,
      smTip,
      smAlerts,
      smAlertN: al.length + ' avisos · ' + al.filter(a => a.s === 3).length + ' de prioridad alta',
      smNoAlerts: !al.length,
      smCols: cols.map(c => ({
        l: c.l
      })),
      smWinLab: winLab,
      smNorms: seg([['tot', 'Total'], ['ton', 'por t'], ['vis', 'por visita']], norm, 'smNorm'),
      smWins: seg([['hoy', 'Día'], ['7', '7 días'], ['all', 'Período']], win, 'smWin'),
      smCellLeave: () => this.setState({
        smCell: null
      }),
      tlCum,
      tlCumArea,
      tlCumEnd: nf(cq),
      tlCumT: (100 - cq / cMx * 100).toFixed(2) + '%',
      tlBars,
      tlLine: sp.length ? 'M' + sp.join(' L') : 'M0 0',
      tlRef: 'M0 ' + Ys(100) + ' H1000',
      tlRefT: Ys(100) + '%',
      tlAl: [{
        lab: alFmt(aMx / 1.1),
        top: (100 - 1 / 1.1 * 100).toFixed(1) + '%'
      }, {
        lab: '0',
        top: '100%'
      }],
      tlSf: [{
        lab: '130 %',
        top: '0%'
      }, {
        lab: '100 %',
        top: Ys(100) + '%'
      }, {
        lab: '70 %',
        top: '100%'
      }],
      tlAlLab: alLab,
      tlSel: ((day + 0.5) / N * 100).toFixed(2) + '%',
      tlCur: hv != null,
      tlCurL: hv != null ? ((hv + 0.5) / N * 100).toFixed(2) + '%' : '0',
      tlTip,
      tlX: [0, 0.2, 0.4, 0.6, 0.8, 1].map(f => {
        const d = Math.round(f * (N - 1));
        return {
          lab: D.days[d],
          left: ((d + 0.5) / N * 100).toFixed(2) + '%'
        };
      }),
      tlHits: range(0, N - 1).map(d => ({
        enter: () => this.setState({
          smHov: d
        }),
        click: () => this.setState({
          day: d
        })
      })),
      tlLeave: () => this.setState({
        smHov: null
      })
    };
  }
};
export default logic;
